import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';

// Global: always send cookies with every request
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// In-memory token storage (survives re-renders, cleared on tab close)
let inMemoryToken = null;

// Axios request interceptor: attach token as Authorization header fallback
axios.interceptors.request.use((config) => {
  if (inMemoryToken && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${inMemoryToken}`;
  }
  return config;
});

// Helper to format API error detail
function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const logoutRef = useRef(null);

  // Token sentinel for component APIs (conditional checks, dep arrays)
  const token = user ? 'session' : null;

  // Setup 401 response interceptor (once)
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && logoutRef.current) {
          // Session expired — clear state silently (don't redirect from polling endpoints)
          const url = error.config?.url || '';
          const isPolling = url.includes('transfers/incoming') || url.includes('auth/me');
          if (!isPolling) {
            console.warn('Session expired (401) on:', url);
            inMemoryToken = null;
            logoutRef.current(true); // silent logout
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`);
      setUser(data);
      // If cookie-based auth worked, try to get a fresh token via refresh
      try {
        const refreshRes = await axios.post(`${API_URL}/api/auth/refresh`);
        if (refreshRes.data?.token) {
          inMemoryToken = refreshRes.data.token;
        }
      } catch {
        // Refresh failed — cookie auth still works, just no header fallback
      }
    } catch {
      setUser(null);
      inMemoryToken = null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password }
      );
      // Store JWT in memory for Authorization header fallback
      if (data.token) {
        inMemoryToken = data.token;
      }
      setUser(data.user);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message 
      };
    }
  }, []);

  const logout = useCallback(async (silent = false) => {
    inMemoryToken = null;
    if (!silent) {
      try {
        await axios.post(`${API_URL}/api/auth/logout`, {});
      } catch {
        // Logout errors are non-critical
      }
    }
    setUser(null);
  }, []);

  // Keep logoutRef in sync so the interceptor can call it
  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  const forgotPassword = useCallback(async (email) => {
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message
      };
    }
  }, []);

  const resetPassword = useCallback(async (resetToken, newPassword) => {
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token: resetToken,
        new_password: newPassword
      });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message
      };
    }
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    try {
      await axios.post(
        `${API_URL}/api/auth/change-password`,
        { current_password: currentPassword, new_password: newPassword }
      );
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message
      };
    }
  }, []);

  const contextValue = useMemo(() => ({
    user,
    token,
    loading,
    login,
    logout,
    forgotPassword,
    resetPassword,
    changePassword,
    isAdmin: user?.role === 'admin'
  }), [user, token, loading, login, logout, forgotPassword, resetPassword, changePassword]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
